import * as _ from 'lodash';
import * as hash from 'object-hash';
import React, { FormEvent, useEffect, useReducer, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, BillingActionCreators, CenterActionCreators, IApplicationState, ICenterPackagingDataResponse, IValidateAchRoutingNumberResponse, OrganPackaging } from '@nkr/store/index';
import { IOpoForCenterProfile } from '@nkr/models/index';
import { IErrorItem } from '../../error-item';
import { Button, Col, ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup, Radio } from 'react-bootstrap';
import { FormWrapper } from '../styles';
import { profileNavKey } from '../../profile-nav-key';
import { toast } from 'react-toastify';
import { initialState, initialValidation, reducer } from './state';
import CenterProfileNav from '../edit-nav';

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean,
    hasDataChanges: boolean,
    opoList: IOpoForCenterProfile[],
    onTabClick: (href: string) => void,
    onDataChanged: (hasDataChanged: boolean) => void,
    updateErrors: (list: IErrorItem[]) => void
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number) => Promise<ICenterPackagingDataResponse>,
    updateData: (centerId: number, organPackaging: number, achRoutingNumber: string, achAccountNumber: string, selectedOpo: number, achUpdated: boolean) => Promise<ICenterPackagingDataResponse>,
    validateAchRoutingNumber: (achRoutingNumber: string) => Promise<IValidateAchRoutingNumberResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const PackagingTab: React.FC<TProps> = ({ isAdmin, isAdminUser, centerId, hasDataChanges, opoList, onTabClick, onDataChanged, updateErrors, showLoading, hideLoading, getData, updateData, validateAchRoutingNumber }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [editRoutingNumber, setEditRoutingNumber] = useState(false);
    const [editAccountNumber, setEditAccountNumber] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await showLoading('Getting packaging information, one moment please...');

                const response = await getData(centerId);

                let { organPackaging, achRoutingNumber, achAccountNumber, selectedOpo } = response.data.results;

                if (response.data.success) {
                    dispatch({
                        type: 'initialize',
                        payload: {
                            organPackaging,
                            achRoutingNumber, achAccountNumber,
                            originalAchRoutingNumber: achRoutingNumber,
                            originalAchAccountNumber: achAccountNumber,
                            selectedOpo,
                            validation: initialValidation,
                            hashValue: hash(response.data.results)
                        }
                    });

                    onDataChanged(false);
                }

                hideLoading();
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let updatedValue: any;

        let { name, value } = e.currentTarget;

        if (name == 'organPackaging' || name == 'selectedOpo') {
            updatedValue = parseInt(value);
        }

        if (name == 'achRoutingNumber' || name == 'achAccountNumber') {
            updatedValue = value;
        }

        dispatch({
            type: 'updateField',
            payload: {
                name,
                value: updatedValue
            }
        });

        let data = { ...state, [name]: updatedValue };

        let newHash = hash(data);

        onDataChanged(newHash != state.hashValue);
    }

    const handleEditAccountNumber = () => {
        dispatch({
            type: 'updateField',
            payload: {
                name: 'achAccountNumber',
                value: !editAccountNumber ? '' : state.originalAchAccountNumber
            }
        });

        setEditAccountNumber(!editAccountNumber);
    }

    const save = () => {
        let { organPackaging, achRoutingNumber, achAccountNumber, originalAchRoutingNumber, originalAchAccountNumber, selectedOpo } = state;

        let achUpdated = achRoutingNumber != originalAchRoutingNumber || achAccountNumber != originalAchAccountNumber;

        showLoading('Saving center packaging data, one moment please...').then(() => {
            updateData(centerId, organPackaging, achRoutingNumber, achAccountNumber, selectedOpo, achUpdated).then(response => {
                if (response.data.success) {
                    dispatch({
                        type: 'updateHash',
                        payload: hash(response.data.results)
                    });

                    let { organPackaging, achRoutingNumber, achAccountNumber, selectedOpo } = response.data.results;

                    dispatch({
                        type: 'initialize',
                        payload: {
                            organPackaging,
                            achRoutingNumber, achAccountNumber,
                            originalAchRoutingNumber: achRoutingNumber,
                            originalAchAccountNumber: achAccountNumber,
                            selectedOpo,
                            validation: initialValidation,
                            hashValue: hash(response.data.results)
                        }
                    });

                    onDataChanged(false);

                    setEditRoutingNumber(false);
                    setEditAccountNumber(false);
                }
            }).finally(() => {
                hideLoading();
                toast.success('Center packaging data updated successfully')
            });
        });
    }

    const validateBeforeSave = async () => {
        let validation = _.cloneDeep(initialValidation)

        if (state.organPackaging == OrganPackaging.InHouse) {
            if (state.achRoutingNumber == '') {
                validation.achRoutingNumber.message = 'Please specify the routing number';
            } else {
                let routingNumberValidationResponse = await validateAchRoutingNumber(state.achRoutingNumber);

                if (routingNumberValidationResponse.data.success == false) {
                    validation.achRoutingNumber.message = 'Please specify a valid routing number';
                }
            }

            if (state.achAccountNumber == '') {
                validation.achAccountNumber.message = 'Please specify the account number';
            }
        } else if (state.organPackaging == OrganPackaging.Opo) {
            if (state.selectedOpo == 0) {
                validation.selectedOpo.message = 'Please select an OPO';
            }
        } else {
            validation.organPackaging.message = 'Please select the method of organ packaging';
        }

        let errorList = Object.entries(validation).filter(x => x[1].message != '').map(x => ({ field: x[1].field, desc: x[1].message, show: true }));
        let isValid = errorList.length == 0;

        setDirty(true);

        dispatch({
            type: 'updateValidation',
            payload: validation
        });

        updateErrors(errorList);

        if (isValid) {
            save();
        }
    }

    const sortedOpoList = _.sortBy(opoList, x => x.optnName);

    return (
        <>
            <CenterProfileNav
                activeKey={profileNavKey.PACKAGING}
                isAdmin={isAdmin}
                isAdminUser={isAdminUser}
                centerId={centerId}
                hasChanges={hasDataChanges}
                onTabClick={onTabClick}
                onSave={validateBeforeSave}
            />

            <div style={{ padding: '20px 10px 10px 10px', borderWidth: '1px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderRadius: 4, minHeight: 500 }}>
                <FormWrapper horizontal>
                    <FormGroup validationState={dirty && state.validation.organPackaging.message != '' ? 'error' : null}>
                        <Col xs={12} sm={4} componentClass={ControlLabel}>Who performs your kidney packaging for living donors?</Col>
                        <Col xs={12} sm={8}>
                            <Radio inline name='organPackaging' id='organPackagingInHouse' value={1} checked={state.organPackaging == 1} onChange={handleFieldChange}>In-House</Radio>
                            <Radio inline name='organPackaging' id='organPackagingOpo' value={2} checked={state.organPackaging == 2} onChange={handleFieldChange}>OPO</Radio>

                            {dirty && state.validation.organPackaging.message != '' && (
                                <HelpBlock>{state.validation.organPackaging.message}</HelpBlock>
                            )}
                        </Col>
                    </FormGroup>

                    {state.organPackaging == OrganPackaging.InHouse && (
                        <>
                            <FormGroup>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Packaging Remittance Information</Col>
                                <Col xs={12} sm={8}></Col>
                            </FormGroup>
                            <FormGroup validationState={dirty && state.validation.achRoutingNumber.message != '' ? 'error' : null}>
                                <Col xs={12} sm={4} componentClass={ControlLabel} style={{ fontWeight: 'normal' }}>ACH Routing Number:</Col>
                                <Col xs={12} sm={3}>
                                    <InputGroup>
                                        <FormControl
                                            name='achRoutingNumber'
                                            disabled={editRoutingNumber == false}
                                            value={state.achRoutingNumber}
                                            onChange={handleFieldChange}
                                        />
                                        <InputGroup.Button>
                                            <Button style={{ width: 70 }} onClick={() => setEditRoutingNumber(!editRoutingNumber)}>{!editRoutingNumber ? 'Edit' : 'Cancel'}</Button>
                                        </InputGroup.Button>
                                    </InputGroup>

                                    {dirty && state.validation.achRoutingNumber.message != '' && (
                                        <HelpBlock>{state.validation.achRoutingNumber.message}</HelpBlock>
                                    )}
                                </Col>
                            </FormGroup>
                            <FormGroup validationState={dirty && state.validation.achAccountNumber.message != '' ? 'error' : null}>
                                <Col xs={12} sm={4} componentClass={ControlLabel} style={{ fontWeight: 'normal' }}>ACH Account Number:</Col>
                                <Col xs={12} sm={3}>
                                    <InputGroup>
                                        <FormControl
                                            name='achAccountNumber'
                                            disabled={editAccountNumber == false}
                                            value={state.achAccountNumber}
                                            onChange={handleFieldChange}
                                        />
                                        <InputGroup.Button>
                                            <Button style={{ width: 70 }} onClick={handleEditAccountNumber}>{!editAccountNumber ? 'Edit' : 'Cancel'}</Button>
                                        </InputGroup.Button>
                                    </InputGroup>

                                    {dirty && state.validation.achAccountNumber.message != '' && (
                                        <HelpBlock>{state.validation.achAccountNumber.message}</HelpBlock>
                                    )}
                                </Col>
                            </FormGroup>
                        </>
                    )}

                    {state.organPackaging == OrganPackaging.Opo && (
                        <>
                            <FormGroup validationState={dirty && state.validation.selectedOpo.message != '' ? 'error' : null}>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Please specify the OPO:</Col>
                                <Col xs={12} sm={3}>
                                    <FormControl
                                        componentClass='select'
                                        name='selectedOpo'
                                        value={state.selectedOpo}
                                        onChange={handleFieldChange}
                                    >
                                        <option value={0}>- Select OPO -</option>
                                        {sortedOpoList.map(item =>
                                            <option key={'opo-' + item.id} value={item.id}>{item.optnName}</option>
                                        )}
                                    </FormControl>

                                    {dirty && state.validation.selectedOpo.message != '' && (
                                        <HelpBlock>{state.validation.selectedOpo.message}</HelpBlock>
                                    )}
                                </Col>
                            </FormGroup>
                        </>
                    )}
                </FormWrapper>
            </div>
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId) => {
            return dispatch(CenterActionCreators.getPackagingData(centerId));
        },
        updateData: (centerId, organPackaging, achRoutingNumber, achAccountNumber, selectedOpo, achUpdated) => {
            return dispatch(CenterActionCreators.updatePackagingData(
                centerId,
                organPackaging,
                achRoutingNumber,
                achAccountNumber,
                selectedOpo,
                achUpdated
            ));
        },
        validateAchRoutingNumber: (achRoutingNumber) => {
            return dispatch(BillingActionCreators.validateAchRoutingNumber(achRoutingNumber));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(PackagingTab);